import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useMutation, useQuery} from '@apollo/client';
import {Alert, Dropdown, Empty, Form, Input, Table, Tag, Tooltip} from 'antd';
import {Button} from '@dabafinance/react-components';
import moment from 'moment';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import dotsIcon from 'images/dots.svg';
import {DELETE_RECOMMENDATION, GET_RECOMMENDATIONS} from './resolvers';
import {
  errorMessage,
  isGqlError,
  languageLabel,
  readMutation,
  sourceLabel,
} from './constants';
import {RecommendationFormModal} from './RecommendationFormModal';
import styles from './recommendations.module.scss';

/**
 * Instrument Recommendations list.
 *
 * `fetchUnPublished: true` means *all* recommendations rather than only drafts,
 * and the backend sorts by record createdAt DESC — not by recommendation date.
 * The list API has no server-side search, so the filter box below is explicitly
 * scoped to the page on screen.
 */
export const InstrumentRecommendations = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pageFilter, setPageFilter] = useState('');
  const [formModal, setFormModal] = useState({open: false, record: null});
  const [deleteModal, setDeleteModal] = useState({open: false, record: null});

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.InstrumentRecommendations),
  );

  const {data, loading, error, refetch} = useQuery(GET_RECOMMENDATIONS, {
    variables: {page, limit, fetchUnpublished: true},
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const [deleteRecommendation, {loading: deleteLoading}] = useMutation(
    DELETE_RECOMMENDATION,
    {errorPolicy: 'all'},
  );

  const result = data?.getInstrumentRecommendations;
  const unionError = isGqlError(result) ? result : null;
  const rows = useMemo(
    () => (unionError ? [] : result?.data || []),
    [result, unionError],
  );
  const pagination = unionError ? null : result?.pagination;

  const isForbidden =
    unionError?.statusCode === 401 || unionError?.statusCode === 403;

  const visibleRows = useMemo(() => {
    const term = pageFilter.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(row =>
      [row?.name, row?.exchange?.code, sourceLabel(row?.defaultSource)]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(term)),
    );
  }, [rows, pageFilter]);

  const openWorkspace = record =>
    navigate(`/dashboard/stock-recommendations/${record.id}`, {
      state: {recommendation: record},
    });

  const handleDelete = () => {
    deleteRecommendation({variables: {id: deleteModal.record?.id}})
      .then(result => {
        const {payload, error: failure} = readMutation(
          result,
          'deleteInstrumentRecommendation',
        );
        if (failure) return toast.error(failure);
        toast.success(payload.message);
        setDeleteModal({open: false, record: null});
        refetch();
      })
      .catch(mutationError => toast.error(mutationError?.message));
  };

  const columns = [
    {
      title: 'Recommendation',
      dataIndex: 'name',
      render: (text, record) => (
        <div className={styles.nameCell}>
          <span className={styles.primaryText}>{record?.name}</span>
          <div className={styles.chips}>
            {record?.multiLang?.map(entry => (
              <Tag key={entry?.language} className={styles.softTag}>
                {languageLabel(entry?.language)}
              </Tag>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: 'Exchange',
      dataIndex: 'exchange',
      render: (text, record) => (
        <Tooltip title={record?.exchange?.stockExchangeName}>
          <Tag className={styles.softTag}>{record?.exchange?.code || '-'}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Product',
      // Parent `instrumentKey` is never queried — legacy rows stored as STOCK
      // break enum serialization. Writes always send PUBLIC_EQUITY.
      render: () => <span>Public Equity</span>,
    },
    {
      title: 'Recommendation date',
      dataIndex: 'date',
      render: date => (date ? moment(date).format('DD MMM YYYY') : '-'),
    },
    {
      title: 'Original language',
      dataIndex: 'language',
      render: language => (
        <Tag className={styles.softTag}>{languageLabel(language)}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isPublished',
      render: (isPublished, record) => (
        <div className={styles.statusCell}>
          <Tag color={isPublished ? 'green' : 'default'}>
            {isPublished ? 'Published' : 'Draft'}
          </Tag>
          {isPublished && record?.publishedDate && (
            <span className={styles.mutedText}>
              {moment(record.publishedDate).format('DD MMM YYYY')}
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Default source',
      dataIndex: 'defaultSource',
      render: source => (source ? sourceLabel(source) : '-'),
    },
    {
      title: 'Sources',
      dataIndex: 'availableSources',
      render: sources =>
        sources?.length ? (
          <div className={styles.chips}>
            {sources.map(source => (
              <Tag key={source?.name} className={styles.softTag}>
                {sourceLabel(source?.name)}
              </Tag>
            ))}
          </div>
        ) : (
          <span className={styles.mutedText}>None</span>
        ),
    },
    {
      title: 'Last updated',
      dataIndex: 'lastUpdated',
      render: lastUpdated =>
        lastUpdated ? moment(lastUpdated).fromNow() : '-',
    },
    {
      title: '',
      key: 'actions',
      render: (text, record) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'open',
                label: 'Open workspace',
                onClick: event => {
                  event.domEvent.stopPropagation();
                  openWorkspace(record);
                },
              },
              {
                key: 'edit',
                // Published name/date/translations appear in every generated
                // PDF, and editing them does not rebuild those reports — so
                // published metadata stays read-only.
                label: record?.isPublished
                  ? 'Edit metadata (published — read-only)'
                  : 'Edit metadata',
                disabled: !tabOperations?.update || record?.isPublished,
                onClick: event => {
                  event.domEvent.stopPropagation();
                  setFormModal({open: true, record});
                },
              },
              ...(record?.isPublished
                ? []
                : [
                    {
                      key: 'publish',
                      // Publication runs the preflight checklist in the
                      // workspace, where pick and report data is available.
                      label: 'Review and publish',
                      disabled: !tabOperations?.update,
                      onClick: event => {
                        event.domEvent.stopPropagation();
                        navigate(
                          `/dashboard/stock-recommendations/${record.id}`,
                          {state: {recommendation: record, openPublish: true}},
                        );
                      },
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      danger: true,
                      disabled: !tabOperations?.delete,
                      onClick: event => {
                        event.domEvent.stopPropagation();
                        setDeleteModal({open: true, record});
                      },
                    },
                  ]),
            ],
          }}>
          <img
            src={dotsIcon}
            alt="actions"
            className={styles.dotsIcon}
            onClick={event => event.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  if (isForbidden) {
    return (
      <div className={styles.container}>
        <Alert
          type="error"
          showIcon
          message="You do not have access to Instrument Recommendations"
          description={errorMessage(
            unionError,
            'Ask an administrator for the Instrument Recommendations entitlement.',
          )}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Instrument Recommendations</h1>
          <p>Public Equity picks, reports and publication</p>
        </div>
        <Button
          label="Create Recommendation"
          onClick={() => setFormModal({open: true, record: null})}
          disabled={!tabOperations?.create}
        />
      </div>

      {(unionError || error) && (
        <Alert
          className={styles.alert}
          type="error"
          showIcon
          message={errorMessage(
            unionError,
            error?.message || 'Could not load recommendations',
          )}
          action={<Button label="Retry" type="secondary" onClick={refetch} />}
        />
      )}

      <div className={styles.tableToolbar}>
        <Input.Search
          allowClear
          className={styles.filterInput}
          value={pageFilter}
          placeholder="Filter current page"
          onChange={event => setPageFilter(event.target.value)}
        />
        <span className={styles.mutedText}>
          Filters the {rows.length} rows on this page — the list API has no
          cross-page search.
        </span>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        className={styles.table}
        dataSource={visibleRows}
        locale={{
          emptyText: (
            <Empty
              description={
                pageFilter
                  ? 'No rows on this page match your filter'
                  : 'No instrument recommendations yet'
              }
            />
          ),
        }}
        onRow={record => ({onClick: () => openWorkspace(record)})}
        pagination={{
          current: page,
          pageSize: limit,
          total: pagination?.total || 0,
          showSizeChanger: true,
          showTotal: total => `${total} recommendations`,
          onChange: (nextPage, nextLimit) => {
            setPage(nextPage);
            setLimit(nextLimit);
            setPageFilter('');
          },
        }}
      />

      <DBModal
        isOpen={formModal.open}
        width={720}
        destroyOnClose
        handleClose={() => {
          setFormModal({open: false, record: null});
          form.resetFields();
        }}
        content={
          <RecommendationFormModal
            form={form}
            record={formModal.record}
            onClose={() => {
              setFormModal({open: false, record: null});
              form.resetFields();
            }}
            onSaved={() => refetch()}
          />
        }
      />

      <ConfirmModal
        openModal={deleteModal.open}
        setOpenModal={() => setDeleteModal({open: false, record: null})}
        title="Delete Recommendation"
        confirmText={
          <span>
            This deletes <strong>{deleteModal.record?.name}</strong> and all of
            its picks. Drafts only — published recommendations cannot be
            deleted.
          </span>
        }
        loading={deleteLoading}
        handleOperation={handleDelete}
      />
    </div>
  );
};

export default InstrumentRecommendations;
